import { Autocomplete } from '@mui/material';
import PmiTextField from 'shell/components/shared/text-field';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { __MUNICIPAL_MESSENGER_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';
import debounce from 'shell/utils/debounce';
import { MunicipalMessengerMessengerListItem } from 'shell/models/municipal-messenger-messenger-list';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';

interface AutocompleteMessoProps<T> {
  ambitoTerritoriale?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  id?: string;
  label?: string;
  value?: ItemVM<T> | null;
  onChange: (values: ItemVM<T> | null) => void;
}

const AutocompleteMessoListComponent = <T extends MunicipalMessengerMessengerListItem>({
  value,
  id,
  onChange,
  label,
  disabled = false,
  required = false,
  error,
  helperText,
  ambitoTerritoriale,
}: AutocompleteMessoProps<T>): React.ReactElement => {
  const handleChange = (element: ItemVM<T> | null): void => {
    onChange(element);
  };

  const [messoList, getMessoList] = useHttpFetch<
    HttpResponseList<MunicipalMessengerMessengerListItem>,
    Array<ItemVM<MunicipalMessengerMessengerListItem>>
  >(`${__MUNICIPAL_MESSENGER_API_URL__}/messi/list`, {
    onBootstrap: false,
    options: {
      abortable: true,
    },
    transformer: (response) => {
      return response?.data?.payload?.list?.map((el) => ({
        id: el.id,
        value: el.nominativo,
        el,
      }));
    },
  });

  useEffect(() => {
    if (ambitoTerritoriale) {
      (
        getMessoList({
          params: {
            attivo: true,
            ambitoTerritoriale,
          },
        }) as ResponsePromise<
          HttpResponseList<MunicipalMessengerMessengerListItem>,
          Array<ItemVM<T>>
        >
      ).then((res) => {
        const castedRes = res as Array<ItemVM<T>>;
        handleChange(castedRes[0]);
      });
    }
  }, [ambitoTerritoriale]);

  const handleSearch = debounce<string>(
    useCallback(
      (searchValue) => {
        if (messoList.abort) {
          messoList.abort();
        }

        getMessoList({
          params: {
            messo: searchValue,
          },
        });
      },
      [getMessoList, messoList.abort]
    )
  );

  const messoData = (messoList.data as Array<ItemVM<T>>) || [];

  const defaultProps = useMemo(() => {
    const items = messoData.slice() || [];

    return {
      options: items,
      getOptionLabel: (option: ItemVM<T>) =>
        `${option.id?.toString()} - ${option.value?.toString()}`,
    };
  }, [messoData]);

  return (
    <Autocomplete
      {...defaultProps}
      disabled={disabled}
      id={id}
      value={value}
      onInputChange={(event, changeValue, reason) => {
        if (Boolean(changeValue) && changeValue.length >= 3 && reason === 'input') {
          handleSearch(changeValue);
        }
      }}
      clearOnBlur={false}
      onChange={(event, newValue) => {
        event.stopPropagation();
        handleChange(newValue);
      }}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      renderInput={(params) => (
        <PmiTextField
          {...params}
          error={error}
          helperText={helperText}
          variant="outlined"
          required={required}
          label={label}
        />
      )}
    />
  );
};

AutocompleteMessoListComponent.displayName = 'messo list autocomplete component';

const AutocompleteMessoList = memo(AutocompleteMessoListComponent);

export default AutocompleteMessoList as typeof AutocompleteMessoListComponent;
